import { Bot, ExternalLink, Hash } from "lucide-react";
import { DiscordMarkdown, fillVars, useSampleVars } from "@/components/DiscordMarkdown";

const BTN_STYLE = {
  primary: "bg-[#5865F2] hover:bg-[#4752C4] text-white",
  secondary: "bg-[#4E5058] hover:bg-[#6d6f78] text-white",
  success: "bg-[#248046] hover:bg-[#1a6334] text-white",
  danger: "bg-[#DA373C] hover:bg-[#a12828] text-white",
  link: "bg-[#4E5058] hover:bg-[#6d6f78] text-white",
};

const nowTime = () => new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const Img = ({ src, className }) => (src ? <img src={src} alt="" className={className} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /> : null);

// Anteprima fedele a Discord. I segnaposto ({user}, {server}…) vengono mostrati con valori di esempio.
export function DiscordEmbedPreview({ message, botName = "Il Mio Bot", botAvatar, channelName, fillPlaceholders = true }) {
  const sample = useSampleVars();
  const v = (t) => (fillPlaceholders ? fillVars(t, sample) : t);
  const embeds = message?.embeds || [];
  const buttons = message?.buttons || [];
  const content = v(message?.content);

  return (
    <div className="overflow-hidden rounded-lg bg-[#313338] text-[15px] leading-[1.375rem]" data-testid="embed-preview">
      {channelName && (
        <div className="flex items-center gap-1.5 border-b border-[#1f2023] px-4 py-2.5 text-sm font-semibold text-white">
          <Hash className="h-5 w-5 text-[#80848E]" />{channelName}
        </div>
      )}
      <div className="flex gap-4 p-4 hover:bg-[#2e3035]">
        {botAvatar ? <img src={botAvatar} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" /> : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#5865F2] to-[#8B5CF6]"><Bot className="h-5 w-5 text-white" /></div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-white">{botName}</span>
            <span className="flex h-[15px] items-center rounded-[3px] bg-[#5865F2] px-1 text-[10px] font-semibold text-white">✓ APP</span>
            <span className="ml-1 text-xs text-[#949BA4]">Oggi alle {nowTime()}</span>
          </div>

          {content && <DiscordMarkdown text={content} className="text-[#DBDEE1]" />}

          {embeds.map((em, i) => {
            const color = /^#[0-9a-fA-F]{6}$/.test(em.color || "") ? em.color : "#202225";
            const thumb = v(em.thumbnail);
            const fields = (em.fields || []).filter((f) => f.name || f.value);
            return (
              <div key={i} className="mt-1.5 grid max-w-[520px] rounded-[4px] bg-[#2B2D31]" style={{ borderLeft: `4px solid ${color}` }}>
                <div className="grid gap-2 py-2 pl-3 pr-4" style={{ gridTemplateColumns: thumb ? "minmax(0,1fr) auto" : "minmax(0,1fr)" }}>
                  <div className="min-w-0 space-y-1.5">
                    {em.author?.name && (
                      <div className="flex items-center gap-2 pt-0.5">
                        <Img src={v(em.author.iconUrl)} className="h-6 w-6 rounded-full object-cover" />
                        <span className="text-sm font-semibold text-white">{v(em.author.name)}</span>
                      </div>
                    )}
                    {em.title && (
                      <div className={`font-semibold ${em.url ? "text-[#00A8FC]" : "text-white"}`}>
                        <DiscordMarkdown text={v(em.title)} />
                      </div>
                    )}
                    {em.description && <DiscordMarkdown text={v(em.description)} className="text-sm text-[#DBDEE1]" />}
                    {fields.length > 0 && (
                      <div className="grid gap-x-2 gap-y-2 pt-0.5" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
                        {fields.map((f, fi) => (
                          <div key={fi} style={{ gridColumn: f.inline ? "span 1" : "1 / -1" }} className="min-w-0">
                            <div className="text-sm font-semibold text-white"><DiscordMarkdown text={v(f.name) || "​"} /></div>
                            <DiscordMarkdown text={v(f.value)} className="text-sm text-[#DBDEE1]" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {thumb && <Img src={thumb} className="mt-1 h-20 w-20 rounded object-cover" />}
                  {em.image && (
                    <div className="col-span-full"><Img src={v(em.image)} className="max-h-[300px] w-full rounded object-cover" /></div>
                  )}
                  {(em.footer?.text || em.timestamp) && (
                    <div className="col-span-full flex items-center gap-2 text-xs text-[#949BA4]">
                      <Img src={v(em.footer?.iconUrl)} className="h-5 w-5 rounded-full object-cover" />
                      {em.footer?.text && <span>{v(em.footer.text)}</span>}
                      {em.footer?.text && em.timestamp && <span>•</span>}
                      {em.timestamp && <span>Oggi alle {nowTime()}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {buttons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {buttons.map((b, i) => (
                <button key={i} type="button" className={`flex h-8 items-center gap-1.5 rounded-[3px] px-4 text-sm font-medium transition-colors ${BTN_STYLE[b.action === "link" ? "link" : b.style] || BTN_STYLE.secondary}`}>
                  {b.emoji && <span>{b.emoji}</span>}
                  {b.label || "Bottone"}
                  {b.action === "link" && <ExternalLink className="h-3.5 w-3.5 opacity-80" />}
                </button>
              ))}
            </div>
          )}

          {!content && embeds.length === 0 && buttons.length === 0 && (
            <p className="text-sm italic text-[#949BA4]">L'anteprima del messaggio apparirà qui…</p>
          )}
        </div>
      </div>
    </div>
  );
}
